import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Type, TypeVariant } from "@themes/pollinator/ui/Type";

export type Props = {
  /** Fieldset header/legend text */
  header?: string;
  /** Optional description below the header */
  description?: string;
  /** Typography variant for the header (default: "caption") */
  headerVariant?: TypeVariant;
  /** Typography variant for the description (default: "caption") */
  descriptionVariant?: TypeVariant;
  /** Content/fields within the fieldset */
  children: ReactNode;
  /** Whether to hide the header even if provided */
  hideHeader?: boolean;
  /** Additional CSS classes for the fieldset container */
  className?: string;
  /** Additional CSS classes for the header container */
  headerClassName?: string;
  /** Additional CSS classes for the content container */
  contentClassName?: string;
};

/**
 * A form fieldset component that groups related form fields with an optional header.
 * Uses subtle styling (caption variant) appropriate for inline forms.
 *
 * Used to visually group quiz questions by their header/category within
 * a single-page form layout.
 *
 * @example Basic usage
 * ```tsx
 * <Fieldset header="Personal Information">
 *   <FormField name="firstName" ... />
 *   <FormField name="lastName" ... />
 * </Fieldset>
 * ```
 *
 * @example Without header (for ungrouped questions)
 * ```tsx
 * <Fieldset>
 *   <FormField name="comments" ... />
 * </Fieldset>
 * ```
 */
export function Fieldset({
  header,
  description,
  headerVariant = "h4",
  descriptionVariant = "caption",
  children,
  hideHeader = false,
  className,
  headerClassName,
  contentClassName,
}: Props) {
  const showHeader = !hideHeader && header;

  return (
    <fieldset className={twMerge("flex flex-col gap-6", className)}>
      {showHeader && (
        <div className={twMerge("flex flex-col gap-1", headerClassName)}>
          <Type
            as="legend"
            variant={headerVariant}
            className="text-brand-700 font-semibold"
          >
            {header}
          </Type>
          {description && (
            <Type
              as="p"
              variant={descriptionVariant}
              className="text-brand-700"
            >
              {description}
            </Type>
          )}
        </div>
      )}
      <div className={twMerge("flex flex-col gap-4", contentClassName)}>
        {children}
      </div>
    </fieldset>
  );
}

Fieldset.displayName = "Fieldset";

export default Fieldset;
